import React from "react";
import CollapsibleContainer from "@via/components/collapsible-container/collapsible-container.tsx";
import { HeadingSize } from "@via/components/page-heading.tsx";
import "../file-organizer-form.css";
import { useForm } from "react-hook-form";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { autoMergeFormValidationOptions } from "@via/utils/form-validators.tsx";

interface AutoMergePatternInputProps {
  onAutoMergeClick: (autoMergeInput: string) => void;
}

interface AutoMergeForm {
  autoMergeInput: string;
}

const examplePatterns = [
  `• {name}_L*_R?_001.fastq.gz`,
  `• *-*-{name}_L*_R?_001.fastq.gz`,
  `• *-{name}-{name}_L*_R?_001.fastq.gz`,
];

export const AutoMergePatternInput: React.FC<AutoMergePatternInputProps> = ({
  onAutoMergeClick,
}) => {
  const { control, handleSubmit } = useForm<AutoMergeForm>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      autoMergeInput: "",
    },
  });

  const handleAutoMergeFormSubmit = (payload: AutoMergeForm) => {
    onAutoMergeClick(payload.autoMergeInput);
  };

  const onSubmit = handleSubmit(handleAutoMergeFormSubmit);

  return (
    <div className="collapsible-container-wrapper">
      <CollapsibleContainer
        title="Auto-merging Pattern (Optional)"
        size={HeadingSize.XSmall}>
        <form
          onSubmit={e => {
            e.preventDefault();
            onSubmit(e);
          }}>
          <FormInputComponent
            control={control}
            name="autoMergeInput"
            label="Merge files using pattern"
            placeholder="{name}_L*_R?_001.fastq.gz"
            validations={autoMergeFormValidationOptions.autoMergeInput}
          />

          <div className="mt-2 flex justify-end">
            <button
              data-testid="auto-merge-pattern-button"
              type="submit"
              className="inline-flex whitespace-nowrap justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Add Files Using Auto-Merge Pattern
            </button>
          </div>
        </form>
        <div className="mt-4 flex flex-col gap-2 text-sm text-gray-500">
          <span>
            {
              "Enter a glob pattern containing a {name} placeholder. Files matching the same {name} will be merged automatically."
            }
          </span>
          <span>
            Click Merge Files Using Pattern button for using auto-merge pattern.
          </span>
          <span className="font-semibold">Examples:</span>
          {examplePatterns.map((pattern, index) => (
            <span key={`example-pattern-${index}`}>{pattern}</span>
          ))}
        </div>
      </CollapsibleContainer>
    </div>
  );
};
